import { useCallback, useEffect, useMemo, useState } from 'react';
import { v } from '../constants';
import { SharedHeader } from './SharedHeader';
import { useAuth } from '../auth/AuthContext';
import {
  adminPuzzleAction, fetchAdminReports,
  type AdminAction, type AdminReportRow,
} from '../lib/customPuzzles';
import {
  fetchAdminStats, fetchAdminUsers, grantPremium, revokePremium,
  type AdminStatsResponse, type AdminUserRow,
} from '../lib/admin';

// /admin — single-page dashboard with three tabs:
//   • Overview — daily visit graph + premium rollup
//   • Users    — search + manual premium grant
//   • Reports  — moderation queue (the original Phase 3 surface)
//
// Role check happens server-side on every /admin/* call — anonymous and
// non-admin visitors get a friendly gate. We don't try to gate at the
// route level; the actual data is the source of truth.

type Tab = 'overview' | 'users' | 'reports';

export function AdminDashboard() {
  const auth = useAuth();
  const [tab, setTab] = useState<Tab>('overview');

  if (auth.loading) {
    return <AdminFrame tab={tab} setTab={setTab} showTabs={false}><p style={{ color: v('inkMute') }}>Loading…</p></AdminFrame>;
  }
  if (!auth.user) {
    return (
      <AdminFrame tab={tab} setTab={setTab} showTabs={false}>
        <p>This page is for administrators. Sign in to continue.</p>
        <p><a href="/" className="hm-action" style={{ textDecoration: 'none', padding: '8px 16px' }}>Go to Hexponents</a></p>
      </AdminFrame>
    );
  }

  return (
    <AdminFrame tab={tab} setTab={setTab} showTabs>
      {tab === 'overview' && <OverviewTab />}
      {tab === 'users'    && <UsersTab />}
      {tab === 'reports'  && <ReportsTab />}
    </AdminFrame>
  );
}

// ─── Frame + tab strip ─────────────────────────────────────────────────

function AdminFrame({
  children, tab, setTab, showTabs,
}: {
  children: React.ReactNode;
  tab: Tab; setTab: (t: Tab) => void;
  showTabs: boolean;
}) {
  return (
    <div className="hm-admin">
      <SharedHeader activeView="admin" trailingNav={[{ label: 'Admin', href: '/admin' }]} />
      <main className="hm-admin-main">
        {showTabs && (
          <nav className="hm-admin-tabs" role="tablist" aria-label="Admin sections">
            {(['overview', 'users', 'reports'] as Tab[]).map(t => (
              <button
                key={t}
                role="tab"
                aria-selected={tab === t}
                className={`hm-admin-tab ${tab === t ? 'hm-admin-tab-active' : ''}`}
                onClick={() => setTab(t)}
              >{tabLabel(t)}</button>
            ))}
          </nav>
        )}
        {children}
      </main>
    </div>
  );
}

function tabLabel(t: Tab): string {
  if (t === 'overview') return 'Overview';
  if (t === 'users')    return 'Users';
  return 'Reports';
}

// ─── Overview ──────────────────────────────────────────────────────────

type Range = 'day' | 'week' | 'month' | 'year';

function OverviewTab() {
  const [data, setData] = useState<AdminStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<Range>('week');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAdminStats().then(d => {
      if (!alive) return;
      setLoading(false);
      if (!d) { setError("Couldn't load stats. You may not be an admin."); return; }
      setData(d);
    });
    return () => { alive = false; };
  }, []);

  if (error)   return <p style={{ color: v('bad'), fontSize: 14 }}>{error}</p>;
  if (loading) return <p style={{ color: v('inkMute'), fontSize: 14 }}>Loading…</p>;
  if (!data)   return null;

  const series = buildSeries(data, range);
  const totals = totalsForRange(data.visits, range);

  const prem = data.subscriptions.premium;
  const free = data.subscriptions.free;
  const premPct = data.subscriptions.total > 0
    ? Math.round((prem / data.subscriptions.total) * 100) : 0;
  const totalPaid = data.subscriptions.premiumMonthly + data.subscriptions.premiumYearly;
  const yearlyPct = totalPaid > 0
    ? Math.round((data.subscriptions.premiumYearly / totalPaid) * 100) : 0;
  const monthlyPct = totalPaid > 0
    ? 100 - yearlyPct : 0;

  return (
    <>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 600 }}>Overview</h1>
      <p style={{ margin: 0, color: v('inkMute'), fontSize: 13 }}>
        Unique signed-in users active per day. Anonymous visitors aren't tracked.
      </p>

      <div className="hm-admin-stats">
        <StatTile label="Today"     web={totalsFor('day',   data.visits).web} ios={totalsFor('day',   data.visits).ios} />
        <StatTile label="Last 7d"   web={totalsFor('week',  data.visits).web} ios={totalsFor('week',  data.visits).ios} />
        <StatTile label="Last 30d"  web={totalsFor('month', data.visits).web} ios={totalsFor('month', data.visits).ios} />
        <StatTile label="Last 365d" web={totalsFor('year',  data.visits).web} ios={totalsFor('year',  data.visits).ios} />
      </div>

      <div className="hm-admin-range">
        {(['day', 'week', 'month', 'year'] as Range[]).map(r => (
          <button
            key={r}
            type="button"
            className={`hm-admin-range-btn ${range === r ? 'hm-admin-range-active' : ''}`}
            onClick={() => setRange(r)}
          >{rangeLabel(r)}</button>
        ))}
        <span style={{ marginLeft: 'auto', color: v('inkMute'), fontSize: 12 }}>
          {totals.total} unique users · web {totals.web} · app {totals.ios}
        </span>
      </div>

      <VisitsChart series={series} />

      <h2 style={{ margin: '28px 0 8px', fontSize: 18, fontWeight: 600 }}>Subscriptions</h2>
      <div className="hm-admin-sub-grid">
        <div className="hm-admin-sub-card">
          <div className="hm-admin-sub-label">Premium share</div>
          <div className="hm-admin-sub-big">{premPct}%</div>
          <div className="hm-admin-sub-sub">
            {prem.toLocaleString()} of {data.subscriptions.total.toLocaleString()} total
          </div>
          <RatioBar
            segments={[
              { label: 'Premium', value: prem, color: v('accent') },
              { label: 'Free',    value: free, color: v('inkMute') },
            ]}
          />
        </div>
        <div className="hm-admin-sub-card">
          <div className="hm-admin-sub-label">Premium cadence</div>
          <div className="hm-admin-sub-big">{yearlyPct}% yearly</div>
          <div className="hm-admin-sub-sub">
            {data.subscriptions.premiumMonthly.toLocaleString()} monthly ·{' '}
            {data.subscriptions.premiumYearly.toLocaleString()} yearly
          </div>
          <RatioBar
            segments={[
              { label: 'Monthly', value: data.subscriptions.premiumMonthly, color: v('inkSoft') },
              { label: 'Yearly',  value: data.subscriptions.premiumYearly,  color: v('accent') },
            ]}
          />
          {totalPaid === 0 && (
            <p style={{ color: v('inkMute'), fontSize: 12, marginTop: 8 }}>
              No active premium subscriptions yet.
            </p>
          )}
          {totalPaid > 0 && (
            <p style={{ color: v('inkMute'), fontSize: 12, marginTop: 8 }}>
              {monthlyPct}% monthly · {yearlyPct}% yearly
            </p>
          )}
        </div>
      </div>
    </>
  );
}

function rangeLabel(r: Range): string {
  if (r === 'day')   return 'Day';
  if (r === 'week')  return 'Week';
  if (r === 'month') return 'Month';
  return 'Year';
}

// Unified bucket shape the chart renders. The Day view uses 24 hourly
// buckets sourced from hourlyToday; longer ranges use daily buckets
// densified to a fixed window so zero-traffic days still render an
// empty bar instead of a gap.
interface ChartBucket {
  key: string;
  label: string;
  web: number;
  ios: number;
}

function buildSeries(data: AdminStatsResponse, range: Range): ChartBucket[] {
  if (range === 'day') {
    return data.hourlyToday.map(h => ({
      key: `h-${h.hour}`,
      label: formatHour(h.hour),
      web: h.web,
      ios: h.ios,
    }));
  }
  const days = range === 'week' ? 7 : range === 'month' ? 30 : 365;
  const byDay = new Map(data.visits.map(b => [b.day, b]));
  const out: ChartBucket[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    const row = byDay.get(key) ?? { day: key, web: 0, ios: 0 };
    const [, m, dd] = key.split('-');
    out.push({
      key,
      label: `${parseInt(m, 10)}/${parseInt(dd, 10)}`,
      web: row.web,
      ios: row.ios,
    });
  }
  return out;
}

function formatHour(h: number): string {
  if (h === 0)  return '12a';
  if (h === 12) return '12p';
  if (h < 12)   return `${h}a`;
  return `${h - 12}p`;
}

function totalsForRange(
  all: AdminStatsResponse['visits'],
  range: Range,
): { web: number; ios: number; total: number } {
  return totalsFor(range, all);
}

// Sums the user-day buckets across the window. NOTE: a user active on
// multiple days counts multiple times — this is a "user-days" total,
// not a uniques total. For "unique users" rollups across the full
// window we'd need a separate query.
function totalsFor(
  range: Range,
  all: AdminStatsResponse['visits'],
): { web: number; ios: number; total: number } {
  const days = range === 'day' ? 1 : range === 'week' ? 7 : range === 'month' ? 30 : 365;
  const byDay = new Map(all.map(b => [b.day, b]));
  let web = 0, ios = 0;
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    const row = byDay.get(key);
    if (row) { web += row.web; ios += row.ios; }
  }
  return { web, ios, total: web + ios };
}

function StatTile({ label, web, ios }: { label: string; web: number; ios: number }) {
  return (
    <div className="hm-admin-stat">
      <div className="hm-admin-stat-label">{label}</div>
      <div className="hm-admin-stat-value">{(web + ios).toLocaleString()}</div>
      <div className="hm-admin-stat-sub">
        web {web.toLocaleString()} · app {ios.toLocaleString()}
      </div>
    </div>
  );
}

// Inline SVG chart — keeps the bundle off a charting library. Stacked
// bars: web on the bottom, ios on top. Width scales to container.
function VisitsChart({ series }: { series: ChartBucket[] }) {
  const max = Math.max(1, ...series.map(b => b.web + b.ios));
  const H = 180;
  const padX = 30;
  const padTop = 12;
  const padBottom = 28;
  // The SVG uses a 1000px logical width and scales to container via CSS.
  const W = 1000;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;
  const barGap = series.length > 60 ? 1 : series.length > 14 ? 2 : 4;
  const barW = (innerW - barGap * (series.length - 1)) / Math.max(1, series.length);

  // Label every Nth bar so the axis doesn't crowd at year-scale.
  const labelEvery = series.length <= 8 ? 1
    : series.length <= 31 ? Math.ceil(series.length / 7)
    : Math.ceil(series.length / 12);

  return (
    <div className="hm-admin-chart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Active visitors">
        {/* y-axis baseline */}
        <line
          x1={padX} y1={padTop + innerH}
          x2={W - padX} y2={padTop + innerH}
          stroke={v('rule')} strokeWidth={1}
        />
        {/* y-axis max label */}
        <text
          x={padX - 6} y={padTop + 4}
          textAnchor="end"
          fontSize={11} fill={v('inkMute')}
          fontFamily="ui-monospace, SFMono-Regular, monospace"
        >{max}</text>
        <text
          x={padX - 6} y={padTop + innerH + 4}
          textAnchor="end"
          fontSize={11} fill={v('inkMute')}
          fontFamily="ui-monospace, SFMono-Regular, monospace"
        >0</text>

        {series.map((b, i) => {
          const x = padX + i * (barW + barGap);
          const webH = (b.web / max) * innerH;
          const iosH = (b.ios / max) * innerH;
          const yWeb = padTop + innerH - webH;
          const yIos = yWeb - iosH;
          const showLabel = i === series.length - 1 || i % labelEvery === 0;
          return (
            <g key={b.key}>
              <rect
                x={x} y={yWeb} width={barW} height={webH}
                fill={v('inkSoft')}
              >
                <title>{b.label}: {b.web} web · {b.ios} app</title>
              </rect>
              <rect
                x={x} y={yIos} width={barW} height={iosH}
                fill={v('accent')}
              >
                <title>{b.label}: {b.web} web · {b.ios} app</title>
              </rect>
              {showLabel && (
                <text
                  x={x + barW / 2} y={padTop + innerH + 16}
                  textAnchor="middle"
                  fontSize={10} fill={v('inkMute')}
                  fontFamily="ui-monospace, SFMono-Regular, monospace"
                >{b.label}</text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="hm-admin-chart-legend">
        <span><i style={{ background: v('inkSoft') }} /> Web</span>
        <span><i style={{ background: v('accent') }} /> App</span>
      </div>
    </div>
  );
}

function RatioBar({
  segments,
}: { segments: { label: string; value: number; color: string }[] }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <div className="hm-admin-ratio">
      {total === 0 ? (
        <div className="hm-admin-ratio-seg" style={{ flex: 1, background: v('rule') }} />
      ) : segments.map((s, i) => (
        s.value > 0 && (
          <div
            key={i}
            className="hm-admin-ratio-seg"
            title={`${s.label}: ${s.value}`}
            style={{ flex: s.value, background: s.color }}
          />
        )
      ))}
    </div>
  );
}

// ─── Users ─────────────────────────────────────────────────────────────

function UsersTab() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ total: number; users: AdminUserRow[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const limit = 25;

  const reload = useCallback(async (search: string, off: number) => {
    setLoading(true);
    setError(null);
    const data = await fetchAdminUsers({ q: search, offset: off, limit });
    setLoading(false);
    if (!data) { setError("Couldn't load users."); return; }
    setData(data);
  }, []);

  // Debounce search input so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      reload(q, 0);
    }, 200);
    return () => clearTimeout(t);
  }, [q, reload]);

  function nextPage() {
    if (!data) return;
    const next = offset + limit;
    if (next >= data.total) return;
    setOffset(next);
    reload(q, next);
  }
  function prevPage() {
    const next = Math.max(0, offset - limit);
    setOffset(next);
    reload(q, next);
  }

  return (
    <>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 600 }}>Users</h1>
      <p style={{ margin: 0, color: v('inkMute'), fontSize: 13 }}>
        Search by email or display name. Use Grant to flip a user to premium manually
        (won't override a Stripe / Apple subscription).
      </p>

      <input
        type="search"
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Search email or display name…"
        className="hm-admin-search"
      />

      {error && <p style={{ color: v('bad'), fontSize: 14, marginTop: 14 }}>{error}</p>}

      {!error && loading && (
        <p style={{ color: v('inkMute'), fontSize: 14, marginTop: 14 }}>Loading…</p>
      )}

      {!error && !loading && data && data.users.length === 0 && (
        <p style={{ color: v('inkMute'), fontSize: 14, marginTop: 14 }}>No matches.</p>
      )}

      {!error && data && data.users.length > 0 && (
        <>
          <ul className="hm-admin-userlist">
            {data.users.map(u => (
              <UserRow key={u.id} user={u} onAction={() => reload(q, offset)} />
            ))}
          </ul>
          <div className="hm-admin-pager">
            <button
              type="button" className="hm-action"
              disabled={offset === 0}
              onClick={prevPage}
            >‹ Prev</button>
            <span style={{ color: v('inkMute'), fontSize: 12 }}>
              {offset + 1}–{Math.min(offset + limit, data.total)} of {data.total}
            </span>
            <button
              type="button" className="hm-action"
              disabled={offset + limit >= data.total}
              onClick={nextPage}
            >Next ›</button>
          </div>
        </>
      )}
    </>
  );
}

function UserRow({ user, onAction }: { user: AdminUserRow; onAction: () => void }) {
  const [busy, setBusy] = useState(false);
  const isPremium = useMemo(() => {
    if (!user.subscription) return false;
    const { plan, status } = user.subscription;
    return (plan === 'premium_monthly' || plan === 'premium_yearly')
      && (status === 'active' || status === 'trialing');
  }, [user.subscription]);

  async function doGrant(plan: 'premium_monthly' | 'premium_yearly') {
    if (busy) return;
    setBusy(true);
    const r = await grantPremium(user.id, plan);
    setBusy(false);
    if (!r.ok) { alert(`Grant failed: ${r.error}`); return; }
    onAction();
  }

  async function doRevoke() {
    if (busy) return;
    if (!confirm(`Revoke premium for ${user.displayName}?`)) return;
    setBusy(true);
    const r = await revokePremium(user.id);
    setBusy(false);
    if (!r.ok) {
      if (r.error === 'has_provider_subscription') {
        alert("This user has a Stripe or Apple subscription — cancel it through the provider, not the admin panel.");
      } else {
        alert(`Revoke failed: ${r.error}`);
      }
      return;
    }
    onAction();
  }

  return (
    <li className="hm-admin-user">
      <div className="hm-admin-user-main">
        <div className="hm-admin-user-name">
          <a href={`/u/${encodeURIComponent(user.displayName)}`} target="_blank" rel="noreferrer">
            {user.displayName}
          </a>
          {user.role === 'admin' && <span className="hm-admin-badge">admin</span>}
          {isPremium && <span className="hm-admin-badge hm-admin-badge-premium">
            {user.subscription?.plan === 'premium_yearly' ? 'yearly' : 'monthly'}
            {user.subscription?.source === 'stripe' && ' · stripe'}
            {user.subscription?.source === 'apple'  && ' · apple'}
            {user.subscription?.source === 'manual' && ' · manual'}
          </span>}
        </div>
        <div className="hm-admin-user-meta">
          <span>{user.email}</span>
          {!user.emailVerified && <span style={{ color: v('bad') }}> · unverified</span>}
          <span style={{ color: v('inkMute') }}> · joined {new Date(user.createdAt).toLocaleDateString()}</span>
          {user.subscription?.currentPeriodEnd && (
            <span style={{ color: v('inkMute') }}> · expires {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}</span>
          )}
        </div>
      </div>
      <div className="hm-admin-user-actions">
        {!isPremium && (
          <>
            <button type="button" className="hm-action" disabled={busy}
                    onClick={() => doGrant('premium_monthly')}>+ Monthly</button>
            <button type="button" className="hm-action" disabled={busy}
                    onClick={() => doGrant('premium_yearly')}>+ Yearly</button>
          </>
        )}
        {isPremium && (
          <button type="button" className="hm-action" disabled={busy}
                  onClick={doRevoke}>Revoke</button>
        )}
      </div>
    </li>
  );
}

// ─── Reports (existing moderation queue) ───────────────────────────────

function ReportsTab() {
  const [rows, setRows] = useState<AdminReportRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<Record<string, boolean>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const data = await fetchAdminReports();
    setLoading(false);
    if (!data) { setError("Couldn't load reports. You may not be an admin, or the server is down."); return; }
    setRows(data);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function performAction(id: string, action: AdminAction) {
    setActing(prev => ({ ...prev, [id]: true }));
    const ok = await adminPuzzleAction(id, action);
    setActing(prev => ({ ...prev, [id]: false }));
    if (ok) reload();
    else alert(`Couldn't ${action} the puzzle. Try again.`);
  }

  return (
    <>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 600 }}>Moderation queue</h1>
      <p style={{ margin: 0, color: v('inkMute'), fontSize: 13 }}>
        Puzzles with at least one pending report.
      </p>

      {error && <p style={{ marginTop: 18, color: v('bad'), fontSize: 14 }}>{error}</p>}
      {!error && loading && <p style={{ marginTop: 18, color: v('inkMute'), fontSize: 14 }}>Loading…</p>}
      {!error && !loading && rows && rows.length === 0 && (
        <p style={{ marginTop: 18, color: v('inkMute'), fontSize: 14 }}>Nothing pending — inbox zero.</p>
      )}

      {!error && rows && rows.length > 0 && (
        <ul className="hm-admin-list">
          {rows.map(r => (
            <li key={r.id} className="hm-admin-row">
              <header className="hm-admin-row-head">
                <div>
                  <h3>
                    <a href={`/p/${r.id}`} style={{ color: 'inherit' }}>{r.name}</a>
                  </h3>
                  <div className="hm-admin-row-meta">
                    {r.author ? (
                      <>by <strong>{r.author.displayName}</strong> <span style={{ color: v('inkMute') }}>({r.author.email})</span></>
                    ) : <em>anonymous author</em>}
                    <span style={{ color: v('inkMute') }}> · </span>
                    <span>{r.reportCount} {r.reportCount === 1 ? 'report' : 'reports'}</span>
                    <span style={{ color: v('inkMute') }}> · </span>
                    <span>visibility: {r.visibility}</span>
                    {r.cleared && <span style={{ color: v('ok') }}> · cleared</span>}
                  </div>
                </div>
                <div className="hm-admin-row-actions">
                  <button type="button" className="hm-action"
                          onClick={() => performAction(r.id, 'clear')}
                          disabled={acting[r.id]}>Clear</button>
                  {r.visibility === 'public' ? (
                    <button type="button" className="hm-action"
                            onClick={() => performAction(r.id, 'hide')}
                            disabled={acting[r.id]}>Hide</button>
                  ) : (
                    <button type="button" className="hm-action"
                            onClick={() => performAction(r.id, 'unhide')}
                            disabled={acting[r.id]}>Unhide</button>
                  )}
                </div>
              </header>
              {r.reasons.length > 0 && (
                <details className="hm-admin-reasons">
                  <summary>Reasons ({r.reasons.length}{r.reasons.length === 5 && '+'})</summary>
                  <ul>
                    {r.reasons.map((reason, i) => (
                      <li key={i}>
                        {reason.reason ? (
                          <span>"{reason.reason}"</span>
                        ) : (
                          <em style={{ color: v('inkMute') }}>(no reason given)</em>
                        )}
                        <span style={{ color: v('inkMute'), marginLeft: 8, fontSize: 11 }}>
                          {new Date(reason.createdAt).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
